import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import LightningWithArrowIcon from '@svg/lightning-with-arrow.svg';
import LightningIcon from '@svg/lightning.svg';
import {
  FormTemplateAttribute,
  FormTemplateFieldResponse,
} from 'mapistry-shared';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Tooltip } from '../../../../shared/componentsV2/Tooltip';
import { FieldConditionHelpers } from './hooks/useFieldConditionHelpers';
import { EditableFormTemplateField } from './types';

const Qualifiers = styled.div`
  display: flex;
  align-items: center;
`;

const QualifierTooltipContent = styled(IconButton)`
  max-height: 1.5rem;
  padding: 0;
  margin: -0.5rem 0.25rem -0.42rem;
  background-color: ${({ theme }) => theme.colors.grayeee};

  & svg {
    height: 1rem;
  }
`;

function QualifierTooltip({
  title,
  Icon,
}: {
  title: string;
  Icon: React.ElementType;
}) {
  return (
    <Tooltip placement="bottom" title={title}>
      <QualifierTooltipContent>
        <Icon aria-hidden />
      </QualifierTooltipContent>
    </Tooltip>
  );
}

function QualifierIcons({
  field,
  fieldConditionHelpers,
}: FormTemplateFieldPreviewQualifiersProps) {
  const isConditionTriggerField = fieldConditionHelpers.isConditionTriggerField(
    field.id,
  );
  const triggerField =
    fieldConditionHelpers.getTriggerFieldByConditionallyShownFieldId(field.id);

  if (!isConditionTriggerField && !triggerField) {
    return null;
  }

  return (
    <>
      {isConditionTriggerField ? (
        <QualifierTooltip
          title="Triggers conditional field"
          Icon={LightningIcon}
        />
      ) : null}

      {triggerField ? (
        <QualifierTooltip
          title={`Conditionally shown based on "${triggerField.name}" field`}
          Icon={LightningWithArrowIcon}
        />
      ) : null}
    </>
  );
}

function useGetQualifierText(
  field: FormTemplateFieldPreviewQualifiersProps['field'],
) {
  const isInspectionDate = useMemo(
    () => field.attributes?.includes(FormTemplateAttribute.IS_INSPECTION_DATE),
    [field.attributes],
  );

  if (isInspectionDate) {
    return 'Inspection Date';
  }
  return field.isRequired ? 'Required' : null;
}

interface FormTemplateFieldPreviewQualifiersProps {
  field: FormTemplateFieldResponse | EditableFormTemplateField;
  fieldConditionHelpers: FieldConditionHelpers;
}

export function FormTemplateFieldPreviewQualifiers({
  field,
  fieldConditionHelpers,
}: FormTemplateFieldPreviewQualifiersProps) {
  return (
    <Qualifiers>
      <QualifierIcons
        field={field}
        fieldConditionHelpers={fieldConditionHelpers}
      />
      {useGetQualifierText(field)}
    </Qualifiers>
  );
}
